import React, { useState, useEffect, useContext } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import ImageModal from './ImageModal'; // Import the modal
// import propertyManaged from '../assets/property-managed.jpg';
import { trackEvent } from '../utils/analytics';
import { AuthContext } from '../App';

function PropertyDetail() {
  const { id } = useParams(); // Get property ID from URL
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useContext(AuthContext);
  const [property, setProperty] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null); // State for modal
  const [reportReason, setReportReason] = useState('scam');
  const [reportMessage, setReportMessage] = useState('');
  const [reportStatus, setReportStatus] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [viewingSlots, setViewingSlots] = useState([]);
  const [bookingSlotId, setBookingSlotId] = useState(null);
  const [bookingStatus, setBookingStatus] = useState(null);
  const [showAllSlots, setShowAllSlots] = useState(false);
  const [confirmSlot, setConfirmSlot] = useState(null);

  const resolveImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    return `${process.env.REACT_APP_API_BASE_URL}${url}`;
  };

  useEffect(() => {
    const fetchProperty = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/properties/${id}`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setProperty(data.property);

        const settingsRes = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/properties/${id}/settings`);
        if (settingsRes.ok) {
          const settingsData = await settingsRes.json();
          setSettings(settingsData.settings || null);
        }

        // Fetch available viewing slots
        const slotsRes = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/properties/${id}/walkthrough-slots`);
        if (slotsRes.ok) {
          const slotsData = await slotsRes.json();
          const slots = Array.isArray(slotsData) ? slotsData : (slotsData.slots || []);
          // Server already filters to active + unbooked; just filter to future
          const now = new Date();
          setViewingSlots(slots.filter(s => new Date(s.start_time) > now));
        }
      } catch (err) {
        setError(err.message);
        console.error("Error fetching property details:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchProperty();
  }, [id]); // Re-fetch if ID changes

  // Inject JSON-LD structured data for SEO
  useEffect(() => {
    if (!property) return;
    const jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'Residence',
      name: `${property.address}, ${property.city}`,
      description: property.description || `${property.bedrooms} bed, ${property.bathrooms} bath rental in ${property.city}`,
      url: `https://rentual.co/properties/${property.id}`,
      address: {
        '@type': 'PostalAddress',
        streetAddress: property.address,
        addressLocality: property.city,
        addressRegion: property.province || 'ON',
        postalCode: property.postal_code || '',
        addressCountry: 'CA'
      },
      numberOfBedrooms: property.bedrooms,
      numberOfBathroomsTotal: property.bathrooms,
      ...(property.photo_urls?.[0] && { image: property.photo_urls[0].startsWith('http') ? property.photo_urls[0] : `https://rentual.co${property.photo_urls[0]}` }),
      offers: {
        '@type': 'Offer',
        price: property.rent,
        priceCurrency: 'CAD',
        availability: 'https://schema.org/InStock'
      }
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(jsonLd);
    script.id = 'property-jsonld';
    const existing = document.getElementById('property-jsonld');
    if (existing) existing.remove();
    document.head.appendChild(script);
    // Update page title
    document.title = `${property.address}, ${property.city} — ${Number(property.rent).toLocaleString('en-CA', { style: 'currency', currency: 'CAD', minimumFractionDigits: 0 })}/mo | Rentual`;
    return () => { const el = document.getElementById('property-jsonld'); if (el) el.remove(); };
  }, [property]);

  useEffect(() => {
    if (!property?.id) return;
    trackEvent({
      event_name: 'property_view',
      event_category: 'property',
      event_action: 'view',
      property_id: property.id,
      metadata: { city: property.city, status: property.status }
    });
  }, [property]);

  // Auto-open confirmation if bookSlot param is in URL (returning from login/register)
  useEffect(() => {
    const bookSlotId = new URLSearchParams(location.search).get('bookSlot');
    if (bookSlotId && user?.userType === 'tenant' && viewingSlots.length > 0 && !confirmSlot) {
      const slot = viewingSlots.find(s => String(s.id) === bookSlotId);
      if (slot) {
        setConfirmSlot(slot);
        const params = new URLSearchParams(location.search);
        params.delete('bookSlot');
        const cleanUrl = params.toString() ? `${location.pathname}?${params.toString()}` : location.pathname;
        window.history.replaceState({}, '', cleanUrl);
      }
    }
  }, [user, viewingSlots, location.search, location.pathname, confirmSlot]);

  if (loading) {
    return <main className="App-main">Loading property details...</main>;
  }

  if (error) {
    return <main className="App-main"><p className="error-message">Error: {error}</p></main>;
  }

  if (!property) {
    return <main className="App-main"><p>Property not found.</p></main>;
  }

  const utilities = settings?.utilities_included || {};
  const utilitiesList = ['water', 'heat', 'hydro', 'internet'].filter((key) => utilities[key]);
  const utilitiesText = utilitiesList.length ? utilitiesList.map((u) => u.charAt(0).toUpperCase() + u.slice(1)).join(', ') : 'Not included';
  const utilitiesNotes = utilities?.notes ? ` — ${utilities.notes}` : '';
  const parkingText = settings?.parking_included ? (settings.parking_details || 'Included') : 'Not included';
  const smokingText = settings?.smoking_allowed === true || settings?.smoking_allowed === 'yes'
    ? 'Yes'
    : settings?.smoking_allowed === false || settings?.smoking_allowed === 'no'
      ? 'No'
      : 'Not specified';
  // inviteToken kept in URL for tracking but no longer gates actions
  // eslint-disable-next-line no-unused-vars
  const inviteToken = new URLSearchParams(location.search).get('invite');

  const getVerificationBadge = () => {
    if (!property) return { label: 'Unverified landlord', tone: 'warning' };
    if (property.landlord_ownership_status === 'approved' || property.landlord_id_status === 'verified') {
      return { label: 'Verified landlord', tone: 'paid' };
    }
    return { label: 'Unverified landlord', tone: 'warning' };
  };

  const handleApply = () => {
    if (!user || user.userType !== 'tenant') {
      const redirect = encodeURIComponent(`${location.pathname}${location.search}`);
      navigate(`/login?redirect=${redirect}`);
      return;
    }
    trackEvent({
      event_name: 'application_started',
      event_category: 'application',
      event_action: 'apply_click',
      property_id: property.id
    });
    navigate(`/apply/${id}`);
  };

  const handleSlotClick = (slotId) => {
    if (!user || user.userType !== 'tenant') {
      // Embed selected slot in redirect URL so it survives login/register
      const params = new URLSearchParams(location.search);
      params.set('bookSlot', slotId);
      const redirectPath = `${location.pathname}?${params.toString()}`;
      navigate(`/login?redirect=${encodeURIComponent(redirectPath)}`);
      return;
    }
    // Show confirmation
    const slot = viewingSlots.find(s => s.id === slotId);
    setConfirmSlot(slot);
    setBookingStatus(null);
  };



  const handleConfirmBooking = async () => {
    if (!confirmSlot) return;
    setBookingSlotId(confirmSlot.id);
    setBookingStatus(null);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/properties/${id}/book-viewing`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${user.token}` },
        body: JSON.stringify({ slotId: confirmSlot.id })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to book viewing');
      setBookingStatus('confirmed');
      setViewingSlots(prev => prev.filter(s => s.id !== confirmSlot.id));
    } catch (err) {
      setBookingStatus(err.message);
    } finally {
      setBookingSlotId(null);
    }
  };

  const submitReport = async () => {
    setReportStatus(null);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/properties/${id}/report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: reportReason, message: reportMessage })
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Unable to submit report');
      }
      setReportStatus('Thanks — report submitted.');
      setReportMessage('');
    } catch (err) {
      setReportStatus(err.message);
    }
  };

  return (
    <main className="App-main property-detail-page">
      <div className="property-detail-card">
        {property.photo_urls && property.photo_urls.length > 0 && (
          <div className="property-images">
            {property.photo_urls.map((url, index) => (
              <img 
                key={index} 
                src={resolveImageUrl(url)} 
                alt={`${property.address} ${index + 1}`} 
                className="property-detail-image" 
                onClick={() => setSelectedImage(resolveImageUrl(url))}
                style={{ cursor: 'pointer' }}
              />
            ))}
          </div>
        )}
        <div className="property-detail-info">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
            <p className="eyebrow-tag" style={{ marginBottom: 0 }}>Listing</p>
            <Link to="/verification" className={`status-pill ${getVerificationBadge().tone}`} title="Learn what landlord verification means">
              {getVerificationBadge().label}
            </Link>
          </div>
          <h2>{property.address}, {property.city}</h2>
          <p className="property-detail-rent">{Number(property.rent).toLocaleString('en-CA', { style: 'currency', currency: 'CAD', minimumFractionDigits: 0, maximumFractionDigits: 0 })}/month</p>
          <p><strong>Province:</strong> {property.province}</p>
          <p><strong>Postal Code:</strong> {property.postal_code}</p>
          <div className="property-specs">
            <span>{property.bedrooms} Bed</span>
            <span>{property.bathrooms} Bath</span>
            <span>{property.property_type}</span>
          </div>
          <p><strong>Amenities:</strong> {property.amenities || 'N/A'}</p>
          <p><strong>Description:</strong> {property.description}</p>
          <p><strong>Status:</strong> {property.status}</p>
          <p style={{ marginTop: '1rem' }}><strong>Payment method:</strong> {settings?.payment_method || 'Not specified'}</p>
          <p><strong>Utilities:</strong> {utilitiesText}{utilitiesNotes}</p>
          <p><strong>Parking:</strong> {parkingText}</p>
          <p><strong>Smoking allowed:</strong> {smokingText}</p>
          <p><strong>Appliances:</strong> {settings?.appliances || 'Not specified'}</p>
          <p><strong>Furnishings:</strong> {settings?.furnishings || 'Not specified'}</p>
          <p><strong>Additional terms:</strong> {settings?.additional_terms || 'None'}</p>

          {viewingSlots.length > 0 && (() => {
            const previewSlots = viewingSlots.slice(0, 3);
            const hasMore = viewingSlots.length > 3;

            const renderSlotRow = (slot) => {
              const start = new Date(slot.start_time);
              const timeStr = start.toLocaleTimeString('en-CA', { timeZone: 'America/Toronto', hour: 'numeric', minute: '2-digit' });
              const dateStr = start.toLocaleDateString('en-CA', { timeZone: 'America/Toronto', weekday: 'short', month: 'short', day: 'numeric' });
              return (
                <div key={slot.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 0.75rem', background: 'var(--surface-color, #f5f8ff)', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
                  <span style={{ fontSize: '0.9rem' }}>{dateStr}, {timeStr}</span>
                  <button className="primary-button" style={{ padding: '0.3rem 0.75rem', fontSize: '0.8rem' }} onClick={() => handleSlotClick(slot.id)} disabled={bookingSlotId === slot.id}>
                    {bookingSlotId === slot.id ? 'Booking...' : 'Book'}
                  </button>
                </div>
              );
            };

            return (
              <div className="dashboard-section" style={{ marginTop: '1.5rem', marginBottom: '1rem' }}>
                <h3 style={{ margin: '0 0 0.75rem' }}>Open viewings</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {previewSlots.map(renderSlotRow)}
                </div>
                {hasMore && (
                  <button type="button" className="secondary-button" style={{ marginTop: '0.75rem', fontSize: '0.85rem' }} onClick={() => setShowAllSlots(true)}>
                    View all {viewingSlots.length} available times
                  </button>
                )}
                {bookingStatus === 'confirmed' && (
                  <p style={{ color: '#059669', marginTop: '0.5rem', fontSize: '0.9rem' }}>Viewing booked! Check your email for confirmation.</p>
                )}
                {bookingStatus && bookingStatus !== 'confirmed' && (
                  <p style={{ color: 'var(--error-color, #ef4444)', marginTop: '0.5rem', fontSize: '0.9rem' }}>{bookingStatus}</p>
                )}

              </div>
            );
          })()}

          <div className="property-actions">
            <button className="primary-button" onClick={handleApply}>Apply Now</button>
            <Link to="/properties" className="secondary-button">Back to Properties</Link>
          </div>

          {/* Share buttons */}
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.75rem' }}>
            <button className="secondary-button" style={{ fontSize: '0.85rem', padding: '0.4rem 0.75rem' }} onClick={() => {
              const url = window.location.href.split('?')[0];
              navigator.clipboard.writeText(url).then(() => {
                const btn = document.activeElement;
                const orig = btn.textContent;
                btn.textContent = '✓ Copied!';
                setTimeout(() => { btn.textContent = orig; }, 2000);
              }).catch(() => {});
            }}>📋 Copy Link</button>
            <a className="secondary-button" style={{ fontSize: '0.85rem', padding: '0.4rem 0.75rem', textDecoration: 'none' }}
              href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href.split('?')[0])}`}
              target="_blank" rel="noopener noreferrer">Facebook</a>
            <a className="secondary-button" style={{ fontSize: '0.85rem', padding: '0.4rem 0.75rem', textDecoration: 'none' }}
              href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href.split('?')[0])}&text=${encodeURIComponent(`${property.address}, ${property.city} — ${Number(property.rent).toLocaleString('en-CA', { style: 'currency', currency: 'CAD', minimumFractionDigits: 0 })}/mo on Rentual`)}`}
              target="_blank" rel="noopener noreferrer">X / Twitter</a>
            {typeof navigator !== 'undefined' && navigator.share && (
              <button className="secondary-button" style={{ fontSize: '0.85rem', padding: '0.4rem 0.75rem' }} onClick={() => {
                navigator.share({ title: `${property.address}, ${property.city}`, text: `${Number(property.rent).toLocaleString('en-CA', { style: 'currency', currency: 'CAD', minimumFractionDigits: 0 })}/mo`, url: window.location.href.split('?')[0] }).catch(() => {});
              }}>📤 Share</button>
            )}
          </div>
          {property?.landlord_ownership_status !== 'approved' && property?.landlord_id_status !== 'verified' && (
            <p className="status-text" style={{ marginTop: '0.75rem' }}>
              This landlord has not yet completed identity verification.
            </p>
          )}

          <div className="property-managed-panel" style={{ marginTop: '1.25rem' }}>
            <p className="eyebrow-tag">Managed by rentual.co</p>
            <ul>
              <li>Managed listings with shareable links and applicant tracking</li>
              <li>Renewal-ready lease workflow with e‑sign support</li>
              <li>Maintenance tracking and lease document access</li>
            </ul>
          </div>

          <div className="dashboard-section" style={{ marginTop: '1.25rem' }}>
            <div className="section-header" style={{ alignItems: 'center', justifyContent: 'flex-start' }}>
              <button
                type="button"
                className="ghost-button"
                onClick={() => setReportOpen((prev) => !prev)}
              >
                {reportOpen ? 'Hide report form' : 'Report listing'}
              </button>
            </div>
            {reportOpen && (
              <>
                <p className="status-text">Help us keep listings safe and accurate.</p>
                <div className="form-group">
                  <label>Reason</label>
                  <select className="form-control" value={reportReason} onChange={(e) => setReportReason(e.target.value)}>
                    <option value="scam">Scam / impersonation</option>
                    <option value="duplicate">Duplicate listing</option>
                    <option value="wrong_info">Wrong information</option>
                    <option value="harassment">Harassment / unsafe behavior</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Details (optional)</label>
                  <textarea
                    rows="3"
                    className="form-control"
                    value={reportMessage}
                    onChange={(e) => setReportMessage(e.target.value)}
                    placeholder="Share any details that help us review the report."
                  />
                </div>
                <button className="secondary-button" onClick={submitReport}>Submit report</button>
                {reportStatus && <p className="status-text" style={{ marginTop: '0.5rem' }}>{reportStatus}</p>}
              </>
            )}
          </div>
        </div>
      </div>
      {/* All viewing times modal — rendered outside card to avoid overflow:hidden clipping */}
      {showAllSlots && (() => {
        const slotsByDate = viewingSlots.reduce((acc, slot) => {
          const dateKey = new Date(slot.start_time).toLocaleDateString('en-CA', { timeZone: 'America/Toronto', weekday: 'long', month: 'long', day: 'numeric' });
          if (!acc[dateKey]) acc[dateKey] = [];
          acc[dateKey].push(slot);
          return acc;
        }, {});
        return (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }} onClick={() => setShowAllSlots(false)}>
            <div style={{ background: 'var(--background-color, #fff)', borderRadius: '12px', maxWidth: '500px', width: '100%', maxHeight: '80vh', overflow: 'auto', padding: '1.5rem' }} onClick={(e) => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h3 style={{ margin: 0 }}>Available viewing times</h3>
                <button type="button" onClick={() => setShowAllSlots(false)} style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer', color: 'var(--text-color)' }}>✕</button>
              </div>
              {Object.entries(slotsByDate).map(([dateLabel, slots]) => (
                <div key={dateLabel} style={{ marginBottom: '1rem' }}>
                  <p style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: '0.4rem', color: 'var(--text-color)' }}>{dateLabel}</p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                    {slots.map(slot => {
                      const timeStr = new Date(slot.start_time).toLocaleTimeString('en-CA', { timeZone: 'America/Toronto', hour: 'numeric', minute: '2-digit' });
                      return (
                        <button key={slot.id} className="primary-button" style={{ padding: '0.35rem 0.75rem', fontSize: '0.8rem' }} onClick={() => { handleSlotClick(slot.id); }} disabled={bookingSlotId === slot.id}>
                          {bookingSlotId === slot.id ? '...' : timeStr}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
              {bookingStatus === 'confirmed' && (
                <p style={{ color: '#059669', marginTop: '0.5rem', fontSize: '0.9rem' }}>Viewing booked! Check your email for confirmation.</p>
              )}
            </div>
          </div>
        );
      })()}

      {/* Booking confirmation modal — rendered outside card to avoid overflow:hidden clipping */}
      {confirmSlot && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1001, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }} onClick={() => { setConfirmSlot(null); setBookingStatus(null); }}>
          <div style={{ background: 'var(--background-color, #fff)', borderRadius: '12px', maxWidth: '400px', width: '100%', padding: '1.5rem', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
            {bookingStatus === 'confirmed' ? (
              <>
                <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>✓</div>
                <h3 style={{ margin: '0 0 0.5rem' }}>Viewing booked!</h3>
                <p className="status-text" style={{ marginBottom: '1rem' }}>A confirmation has been sent to your email.</p>
                <button className="primary-button" onClick={() => { setConfirmSlot(null); setBookingStatus(null); }}>Done</button>
              </>
            ) : (
              <>
                <h3 style={{ margin: '0 0 0.75rem' }}>Confirm viewing</h3>
                <p style={{ fontSize: '1rem', marginBottom: '0.25rem' }}>
                  {new Date(confirmSlot.start_time).toLocaleDateString('en-CA', { timeZone: 'America/Toronto', weekday: 'long', month: 'long', day: 'numeric' })}
                </p>
                <p style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                  {new Date(confirmSlot.start_time).toLocaleTimeString('en-CA', { timeZone: 'America/Toronto', hour: 'numeric', minute: '2-digit' })}
                </p>
                <p className="status-text" style={{ marginBottom: '1.25rem' }}>{property?.address}, {property?.city}</p>
                {bookingStatus && bookingStatus !== 'confirmed' && (
                  <p style={{ color: 'var(--error-color, #ef4444)', fontSize: '0.9rem', marginBottom: '0.75rem' }}>{bookingStatus}</p>
                )}
                <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center' }}>
                  <button className="primary-button" onClick={handleConfirmBooking} disabled={bookingSlotId === confirmSlot.id}>
                    {bookingSlotId === confirmSlot.id ? 'Booking...' : 'Confirm booking'}
                  </button>
                  <button className="secondary-button" onClick={() => { setConfirmSlot(null); setBookingStatus(null); }}>Cancel</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <ImageModal imageUrl={selectedImage} onClose={() => setSelectedImage(null)} />
    </main>
  );
}

export default PropertyDetail;
